import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild, inject, QueryList,  ViewChildren } from '@angular/core';
import { CorrespondenceItem } from '../../../../models/api/view/claim/documentation/correspondence/correspondence-item';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { ExportService } from '../../../../services/export/export.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { CorrespondenceExportMapper } from '../../../../utilities/correspondence-export-mapper';
import { BaseComponent } from '../../../base/base.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

/** BRD 057292 §3.4.003–3.4.006 — LeaveLink Documentation > Correspondence */
@Component({
  selector: 'app-correspondence',
  imports: [TableComponent, ColumnComponent, PageHeaderComponent],
  template: `
    <app-page-header label="Correspondence" />
    <app-table
      #table
      [dataSource]="dataSource"
      [pageSize]="10"
      [showExport]="true"
      [showFilter]="true"
      [loading]="loading"
      [error]="error"
      recordLabel="correspondence"
      [globalFilterFields]="['documentDescription', 'documentType']"
      (export)="onExport($event)"
    >
      <app-column
        name="documentDescription"
        label="Document description"
        filterType="text"
      />
      <app-column
        name="dateCreated"
        label="Date created"
        format="d"
        filterType="date"
      />
      <app-column
        name="documentType"
        label="Document type"
        filterType="select"
      />
    </app-table>
  `,
  styles: ``,
})
export class CorrespondenceComponent extends BaseComponent implements OnInit {
  private datePipe = inject(DatePipe);
  exportService = inject(ExportService);
  private snackbar = inject(Snackbar);

  @ViewChild('table')
  table: TableComponent;

  @ViewChildren(ColumnComponent)
  columnsList: QueryList<ColumnComponent>;

  loading = false;
  error: unknown = null;
  actionError: unknown = null;
  dataSource: CorrespondenceItem[] = [];

  @HandleLoadingState({ errorProperty: 'error' })
  async ngOnInit(): Promise<void> {
    this.view.hideLoading();
    const claim = this.view.currentClaim();
    const items = await this.api.correspondence.get(
      claim.recordSource,
      claim.recordId,
    );
    this.dataSource = (items ?? []).map((item) => ({
      ...item,
      dateCreated: item.dateCreated ? new Date(item.dateCreated) : null,
    }));
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat): Promise<void> {
    const rows = this.table?.filteredValue ?? this.dataSource;
    const columns = this.columnsList?.toArray() ?? [];

    if (!rows.length) {
      this.snackbar.show(
        'No correspondence to export for the current filters.',
        'Error',
      );
      return;
    }

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Correspondence_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = CorrespondenceExportMapper.toCsvExport(
        rows,
        columns,
        'Correspondence',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = CorrespondenceExportMapper.toExcelExport(
        rows,
        columns,
        'Correspondence',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
